/* Pure helpers used by the Live ASR engine in HifzTest.jsx.
   Kept side-effect-free so they can be unit-tested in Jest. */

const _DIA = /[\u064B-\u065F\u0670\u0640\uFEFF]/g;
const _STOP_SIGNS = /[\u06D6-\u06ED]/g;     // ۖ ۗ ۘ ۙ ۚ ۛ ۜ ۝ ۞ ۟ ۠ ۡ ۢ ۣ ۤ ۥ ۦ ۧ ۨ ۩ ۪ ۫ ۬ ۭ
const _VERSE_NUMS = /[\u06DD\u06DE]/g;       // end-of-ayah marker, start of Rub el-Hizb
const _BRACED_NUMS = /[﴾﴿()[\]{}⟨⟩〈〉]\s*[\d\u0660-\u0669]+\s*[﴾﴿()[\]{}⟨⟩〈〉]/g;
const _BARE_DIGITS = /[\d\u0660-\u0669]+/g;

/** Strip canonical Mushaf stop signs, verse-number markers, diacritics, tatweel,
 *  and normalize alif / yaa / taa-marbuta variants so two strings can be compared
 *  letter-by-letter regardless of tashkeel or recitation marks. */
const _PUNCT = /[.,\u060C;\u061B:!?\u061F'"\u00AB\u00BB\u2018\u2019\u201C\u201D\u2026*\-\u2013\u2014/<>()[\]{}\u27E8\u27E9\u3008\u3009\uFD3E\uFD3F]/g;

export function cleanArabic(s) {
  if (!s) return "";
  return s
    .replace(_BRACED_NUMS, " ")
    .replace(_DIA, "")
    // Stop signs sit INSIDE a word (sukoon ۡ, small high signs) → remove silently
    // so we don't fragment the word. Only verse-end & rub-el-hizb get a space.
    .replace(_STOP_SIGNS, "")
    .replace(_VERSE_NUMS, " ")
    .replace(_BARE_DIGITS, " ")
    // Punctuation is never recited (mirrors _strip_punct in backend/quran_data.py).
    .replace(_PUNCT, "")
    .replace(/[\u0623\u0625\u0622\u0671]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .replace(/\s+/g, " ")
    .trim();
}

export function normWord(s) {
  return cleanArabic(s).toLowerCase();
}

/** Levenshtein edit distance — small/fast version for short Arabic tokens. */
export function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a) return b.length;
  if (!b) return a.length;
  const m = a.length, n = b.length;
  if (Math.abs(m - n) > 4) return Math.abs(m - n);
  const prev = new Array(n + 1);
  const curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= n; j++) prev[j] = curr[j];
  }
  return prev[n];
}

/* ---------------------------------------------------------------------------
   Scoring layer: Mushaf (Uthmani) spelling vs the modern spelling ASR writes.
   Mirrors backend/quran_data.py (_token_variants / _heard_form /
   _collapse_letter_names) — keep the two in sync; the rationale and the
   measurements are documented there. In short: speech recognition writes
   ordinary modern spelling (الكتاب, مالك, آمنوا, إبراهيم, يا أيها) while the
   ayah is Uthmani (ٱلْكِتَٰبُ, مَٰلِكِ, ءَامَنُوا, إِبْرَٰهِـۧمَ, يَٰٓأَيُّهَا), so each
   Mushaf word expands to its legitimate spellings and a heard word counts only
   if it IS one of them. A letter is optional only where the Mushaf itself marks
   it, so a different word (قال for قُلْ, الظالمين for ٱلْعَٰلَمِينَ) never passes.
   cleanArabic/normWord above are unchanged: they mirror normalize_arabic,
   which other features share.
   --------------------------------------------------------------------------- */
const _OPTIONAL_LETTER = {
  "ٰ": "ا", "ۥ": "و", "ۦ": "ي", "ۧ": "ي", "ۨ": "ن",
};
const _SILENT_ALIF_MARK = "۟"; // small high rounded zero over a silent alif
const _LETTER_FOLD = {
  "أ": "ا", "إ": "ا", "آ": "ا", "ٱ": "ا",
  "ى": "ي", "ة": "ه",
  "ء": "", "ؤ": "و", "ئ": "",
};
const _MAX_OPTIONAL = 5;
const _PUNCT_ONE = new RegExp(`^${_PUNCT.source}$`);
const _DIGIT = /[0-9٠-٩۰-۹]/;

/** One character -> its scoring form ("" = not compared: marks, punctuation…). */
export function foldChar(c) {
  const cp = c.charCodeAt(0);
  if ((cp >= 0x064b && cp <= 0x065f) || c === "ٰ" || c === "ـ" || c === "﻿") return "";
  if (cp >= 0x06d6 && cp <= 0x06ed) return "";
  if (_PUNCT_ONE.test(c) || /\s/.test(c) || _DIGIT.test(c)) return "";
  return Object.prototype.hasOwnProperty.call(_LETTER_FOLD, c) ? _LETTER_FOLD[c] : c;
}

/** Scoring form of a heard (modern-spelling) word. Mirrors _heard_form. */
export function heardForm(tok) {
  let out = "";
  for (const c of tok || "") out += foldChar(c);
  return out;
}

const _variantCache = new Map();
/** Every legitimate spelling of one Mushaf word, base spelling first; [] for a
 *  waqf/stop-sign token (not a recited word). Mirrors _token_variants — each rule
 *  fires only in the exact pattern it is for, since a junk spelling that happens
 *  to be another word lets that word through. */
export function wordVariants(raw) {
  if (_variantCache.has(raw)) return _variantCache.get(raw);
  const chars = [...raw];
  const slots = [];
  const src = [];
  const seatDaggers = [];
  let optional = 0;
  let afterTatweel = false;
  const addOptional = (alt) => {
    if (optional < _MAX_OPTIONAL) { optional += 1; slots.push(["", alt]); src.push(""); }
  };
  const last = () => slots[slots.length - 1];
  chars.forEach((c, k) => {
    if (c === "ـ") {
      afterTatweel = true;                                   // carries a seatless hamza
    } else if (Object.prototype.hasOwnProperty.call(_OPTIONAL_LETTER, c)) {
      // Dagger alif directly on a waw / alif-maqsura: the letter is only a seat, read
      // as the long alif (الصلاة). A waw with its own vowel first is a consonant (السماوات).
      if (c === "ٰ" && k > 0 && (chars[k - 1] === "و" || chars[k - 1] === "ى") && slots.length && last().length === 1) {
        seatDaggers.push(slots.length - 1);
        return;
      }
      addOptional(_OPTIONAL_LETTER[c]);
    } else if (c === _SILENT_ALIF_MARK) {
      // a silent alif after a waw may be unwritten: أولو, الربا
      if (slots.length >= 2 && last().length === 1 && last()[0] === "ا"
          && slots[slots.length - 2][0] === "و" && optional < _MAX_OPTIONAL) {
        optional += 1;
        slots[slots.length - 1] = ["ا", ""];
      }
    } else if (c === "ۜ") {                             // small high seen: يبسط
      if (slots.length && last().length === 1 && last()[0] === "ص") slots[slots.length - 1] = ["ص", "س"];
    } else if (c === "ٔ" && afterTatweel) {               // seatless hamza: تسألون
      addOptional("ا");
    } else if (c === "ء") {
      // folded away, except before another hamzated alif: ءَأَنتُمْ = أأنتم
      const nxt = chars.slice(k + 1).find((d) => foldChar(d) || d === "ء") || "";
      if (nxt === "أ" || nxt === "إ") addOptional("ا");
    } else {
      const f = foldChar(c);
      if (f) { slots.push([f]); src.push(c); afterTatweel = false; }
    }
  });
  if (!slots.length) { _variantCache.set(raw, []); return []; }

  let lastLetter = -1;
  slots.forEach((sl, i) => { if (sl[0]) lastLetter = i; });
  seatDaggers.forEach((i) => { if (i < lastLetter) slots[i] = [slots[i][0], "ا"]; });   // mid-word only

  const letters = chars.filter((ch) => foldChar(ch) || ch === "ء");
  const lastL = letters[letters.length - 1];
  const tail = lastL ? raw.slice(raw.lastIndexOf(lastL) + 1) : "";
  const wIdx = raw.lastIndexOf("و");
  const dIdx = wIdx > 0 ? raw.lastIndexOf("ُ", wIdx - 1) : -1;
  if (lastL === "و" && ![...tail].some((h) => h >= "ً" && h <= "ّ") && dIdx === wIdx - 1) {
    addOptional("ا");                                     // plural waw: فاءوا
  }

  let out = [""];
  for (const alts of slots) {
    const next = [];
    for (const p of out) for (const alt of alts) next.push(p + alt);
    out = next;
  }
  out = [...new Set(out.filter(Boolean))];
  for (const v of [...out]) {
    if (/^[وف]?[يتنا]حي$/.test(v)) out.push(v + "ي");            // يحيي
    for (const pre of ["", "و", "ف", "ب"]) {
      if (v === `${pre}اليل`) out.push(`${pre}الليل`);   // الليل
    }
  }
  _variantCache.set(raw, out);
  return out;
}

/** A spelling shared by two DIFFERENT words of the passage counts only for the word
 *  with fewer spellings — the one it is the plain spelling of (in 2:9 the alif-less
 *  spelling of يُخَٰدِعُونَ is the other verb, يَخْدَعُونَ). Mirrors _disambiguate. */
export function disambiguateVariants(list) {
  const same = (x, y) => x.length === y.length && x.every((f, i) => f === y[i]);
  return list.map((v) => {
    const drop = new Set();
    for (const w of list) {
      if (same(w, v) || w.length >= v.length) continue;
      for (const f of v) if (w.includes(f)) drop.add(f);
    }
    const kept = v.filter((f) => !drop.has(f));
    return kept.length ? kept : v;
  });
}

const _MUQATTAAT = new Set(["الم", "المص", "الر", "المر", "كهيعص", "طه", "طسم", "طس", "يس", "ص", "حم", "عسق", "ق", "ن"]);
const _LETTER_NAMES = new Map(Object.entries({
  الف: "ا", لام: "ل", ميم: "م", صاد: "ص", را: "ر", كاف: "ك", ها: "ه",
  يا: "ي", عين: "ع", طا: "ط", سين: "س", حا: "ح", قاف: "ق", نون: "ن",
}));

/** The disjoined letters (الٓمٓ…) carry a maddah; أَلَمْ ("did not") has none. */
export function isMuqattaat(raw) {
  const v = wordVariants(raw);
  return v.length > 0 && raw.includes("ٓ") && _MUQATTAAT.has(v[0]);
}

/** Base spellings of the disjoined-letter words among these Mushaf words. */
export function muqattaatIn(rawWords) {
  return [...new Set((rawWords || []).filter(isMuqattaat).map((w) => wordVariants(w)[0]))];
}

/** ASR spells disjoined letters out by name: الٓمٓ is heard as "ألف لام ميم".
 *  Collapse such a run back into the word it spells (only words in `targets`,
 *  so the vocative يا elsewhere is never rewritten). A run at the END of the
 *  stream that is still only part of such a word is held back (returned in
 *  `held`, not in `tokens`) until the rest arrives. Mirrors _collapse_letter_names. */
export function collapseMuqattaat(tokens, targets) {
  if (!targets || !targets.length) return { tokens, held: 0 };
  const out = [];
  let i = 0;
  while (i < tokens.length) {
    let letters = "";
    let j = i;
    let hit = null;
    while (j < tokens.length && _LETTER_NAMES.has(heardForm(tokens[j]))) {
      letters += _LETTER_NAMES.get(heardForm(tokens[j]));
      j += 1;
      if (targets.includes(letters)) hit = [j, letters];
      if (!targets.some((t) => t.startsWith(letters))) break;
    }
    if (hit) { out.push(hit[1]); i = hit[0]; continue; }
    if (j === tokens.length && j > i && targets.some((t) => t !== letters && t.startsWith(letters))) {
      return { tokens: out, held: tokens.length - i };
    }
    out.push(tokens[i]);
    i += 1;
  }
  return { tokens: out, held: 0 };
}

const _asList = (e) => (Array.isArray(e) ? e : e ? [e] : []);

/** A heard word counts as recited right only if it IS one of the expected
 *  word's legitimate spellings (`exp`: a spelling or a wordVariants() list).
 *  No edit-distance tolerance: once the Mushaf/modern spelling differences are
 *  handled by wordVariants, one letter off is a different word — measured over
 *  Al-Fatihah + Al-Baqarah, tolerating one edit on long words let 60 of 61
 *  near-miss wrong words through (الظالمين for ٱلْعَٰلَمِينَ, يخادعون for
 *  يَخْدَعُونَ, المتقون for ٱلْمُتَّقِينَ). */
export function tajweedMatch(tok, exp) {
  if (!tok) return false;
  return _asList(exp).includes(tok);
}

/** Does `tok` cover expected word i (+ how many words / tokens it used)? */
function _matchAt(wordNorms, tokens, t, i) {
  const tok = tokens[t];
  const exp = _asList(wordNorms[i]);
  if (!exp.length) return null;
  // Two Mushaf words run together into one heard token (بسمالله).
  if (i + 1 < wordNorms.length) {
    const nxt = _asList(wordNorms[i + 1]);
    if (exp.some((a) => nxt.some((b) => a + b === tok))) return { words: 2, toks: 1 };
  }
  if (exp.includes(tok)) return { words: 1, toks: 1 };
  // One Mushaf word written as two heard tokens (يَٰٓأَيُّهَا -> يا أيها).
  if (t + 1 < tokens.length && exp.includes(tok + tokens[t + 1])) return { words: 1, toks: 2 };
  return null;
}

/** Live-ASR stream matcher. Walks the heard tokens (from `startTokenIdx`)
 *  against the expected words (from `startCursor`) and returns
 *    { cursor, consumed, mistakes: [{idx, heard}], pending: {idx, heard} | null }.
 *  - a matching token advances the cursor;
 *  - on a mismatch, the tokens that follow decide between: the current word was
 *    skipped (mistake with heard ""); a wrong word then carrying on with the next
 *    word; or a slip the student fixed straight away. A wrong word keeps the cursor
 *    on that word (the student may retry it), but the following tokens are NOT
 *    discarded. (Discarding them turned one slip into the rest of the ayah being
 *    flagged.) `pending` is the mistake still on the current word, if any;
 *  - repeating the word just recited is ignored (not a mistake);
 *  - the last token of an unfinished phrase (`opts.lastTokenProvisional`), or a
 *    last token that is only the start of the expected word (يا of يا أيها), is
 *    left unconsumed until more arrives. A mistake is never judged on it.
 *  Pure and synchronous so it can be unit-tested. */
export function advanceStream(wordNorms, tokens, startCursor = 0, startTokenIdx = 0, opts = {}) {
  let cursor = startCursor;
  let t = startTokenIdx;
  const mistakes = [];
  let pending = null;
  while (t < tokens.length && cursor < wordNorms.length) {
    const exp = _asList(wordNorms[cursor]);
    if (!exp.length) { cursor += 1; continue; }
    const tok = tokens[t];
    if (!tok) { t += 1; continue; }
    const isLast = t === tokens.length - 1;
    const m = _matchAt(wordNorms, tokens, t, cursor);
    if (m) { cursor += m.words; t += m.toks; pending = null; continue; }
    const startsWord = exp.some((v) => v.length > tok.length && v.startsWith(tok));
    if (isLast && (opts.lastTokenProvisional || startsWord)) break;   // wait for the rest

    // A mismatch. Decide what happened from the words that follow (as many as have
    // arrived): whichever reading lines up with more of the ayah wins, ties keep the
    // earlier one. Fixed rules misread ayahs with a repeated short word — dropping
    // one of the three ثم in 2:28 flagged five more correct words.
    const hasNext = cursor + 1 < wordNorms.length;
    const [reading, agreed] = [
      ["retry", _agree(wordNorms, tokens, t + 1, cursor)],                   // slip/repeat fixed straight away
      ["wrong", hasNext ? _agree(wordNorms, tokens, t + 1, cursor + 1) : 0], // wrong word, then carried on
      ["skip", hasNext ? _agree(wordNorms, tokens, t, cursor + 1) : 0],      // current word skipped
    ].reduce((best, r) => (r[1] > best[1] ? r : best));

    if (reading === "skip" && agreed > 0) {
      if (!pending || pending.idx !== cursor) mistakes.push({ idx: cursor, heard: "" });
      pending = null;
      cursor += 1;
      continue;
    }
    // Repeating the word just recited (a restart, "ٱللَّهِ ٱللَّهِ") is not a mistake.
    if (reading === "retry" && cursor > 0 && _asList(wordNorms[cursor - 1]).includes(tok)) {
      t += 1;
      continue;
    }
    // Wrong word for the current word. The cursor stays (the student may retry it)
    // unless they've clearly carried on with the next word.
    if (!pending || pending.idx !== cursor) mistakes.push({ idx: cursor, heard: tok });
    pending = { idx: cursor, heard: tok };
    t += 1;
    if (reading === "wrong" && agreed > 0) {
      pending = null;
      cursor += 1;
    }
  }
  return { cursor, consumed: t, mistakes, pending };
}

/** How many expected words (from c) the heard tokens (from t) agree with in a row,
 *  looking at most `window` words ahead. */
function _agree(wordNorms, tokens, t, c, window = 3) {
  let n = 0;
  while (n < window && t < tokens.length && c < wordNorms.length) {
    const m = _matchAt(wordNorms, tokens, t, c);
    if (!m) break;
    n += m.words;
    t += m.toks;
    c += m.words;
  }
  return n;
}
